/**
 * TanStack Query hooks for CourseMaterial resource.
 * Includes composite upload hook: requestUploadUrl → XHR PUT → confirm.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { api } from '@client/lib/api-client';
import { ALLOWED_CONTENT_TYPES, MAX_FILE_BYTES } from '@shared/schemas/course-material-schemas';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
export interface CourseMaterial {
  id: string;
  courseLessonId: string;
  type: 'file' | 'link' | 'video' | 'document' | 'audio' | 'slide';
  title: string;
  url?: string | null;
  fileKey?: string | null;
  fileSize?: number | null;
  description?: string | null;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

interface UploadUrlResponse {
  materialId: string;
  uploadUrl: string;
  fileKey: string;
}

// ---------------------------------------------------------------------------
// Query Keys
// ---------------------------------------------------------------------------
export const materialKeys = {
  all: ['course-materials'] as const,
  byLesson: (courseLessonId: string) => ['course-materials', { courseLessonId }] as const,
};

// ---------------------------------------------------------------------------
// List / CRUD hooks
// ---------------------------------------------------------------------------
export function useCourseMaterials(courseLessonId: string) {
  return useQuery({
    queryKey: materialKeys.byLesson(courseLessonId),
    queryFn: () =>
      api.get<CourseMaterial[]>('/api/course-materials', { courseLessonId, pageSize: 100 }),
    enabled: !!courseLessonId,
  });
}

export function useCreateLinkMaterial() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: unknown) => api.post<CourseMaterial>('/api/course-materials', data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: materialKeys.all });
    },
  });
}

export function useUpdateMaterial(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: unknown) => api.patch<CourseMaterial>(`/api/course-materials/${id}`, data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: materialKeys.all });
    },
  });
}

export function useDeleteMaterial() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/api/course-materials/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: materialKeys.all });
    },
  });
}

export function useReorderMaterials(courseLessonId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (items: { id: string; sortOrder: number }[]) =>
      api.post<void>('/api/course-materials/reorder', { items }),
    onMutate: async (items) => {
      await qc.cancelQueries({ queryKey: materialKeys.byLesson(courseLessonId) });
      const prev = qc.getQueryData<CourseMaterial[]>(materialKeys.byLesson(courseLessonId));
      if (prev) {
        const updated = prev.map((m) => {
          const found = items.find((i) => i.id === m.id);
          return found ? { ...m, sortOrder: found.sortOrder } : m;
        });
        qc.setQueryData(
          materialKeys.byLesson(courseLessonId),
          [...updated].sort((a, b) => a.sortOrder - b.sortOrder),
        );
      }
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) qc.setQueryData(materialKeys.byLesson(courseLessonId), ctx.prev);
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: materialKeys.byLesson(courseLessonId) });
    },
  });
}

// ---------------------------------------------------------------------------
// Download URL
// ---------------------------------------------------------------------------
export function useDownloadUrl() {
  return useMutation({
    mutationFn: (materialId: string) =>
      api.get<{ url: string }>(`/api/course-materials/${materialId}/download-url`),
  });
}

// ---------------------------------------------------------------------------
// Composite upload hook
// ---------------------------------------------------------------------------
export type UploadStatus = 'idle' | 'requesting' | 'uploading' | 'confirming' | 'done' | 'error';

export interface UploadMaterialParams {
  courseLessonId: string;
  title: string;
  type: CourseMaterial['type'];
  description?: string;
  file: File;
}

export interface UseUploadMaterialReturn {
  upload: (params: UploadMaterialParams) => Promise<void>;
  progress: number;
  status: UploadStatus;
  error: string | null;
  reset: () => void;
}

/** Client-side validation */
export function validateMaterialFile(file: File): string | null {
  if (file.size > MAX_FILE_BYTES) {
    return `File phải nhỏ hơn ${MAX_FILE_BYTES / 1024 / 1024} MB`;
  }
  if (!ALLOWED_CONTENT_TYPES.includes(file.type as (typeof ALLOWED_CONTENT_TYPES)[number])) {
    return `Loại file không được hỗ trợ: ${file.type || 'unknown'}`;
  }
  return null;
}

export function useUploadMaterial(): UseUploadMaterialReturn {
  const qc = useQueryClient();
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState<UploadStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  // Track materialId for rollback if confirm fails
  const materialIdRef = useRef<string | null>(null);

  function reset() {
    setProgress(0);
    setStatus('idle');
    setError(null);
    materialIdRef.current = null;
  }

  async function upload(params: UploadMaterialParams): Promise<void> {
    const { courseLessonId, title, type, description, file } = params;

    const validationErr = validateMaterialFile(file);
    if (validationErr) {
      setError(validationErr);
      setStatus('error');
      return;
    }

    setError(null);
    setProgress(0);
    materialIdRef.current = null;

    try {
      // Step 1 — Request pre-signed upload URL
      setStatus('requesting');
      const { materialId, uploadUrl, fileKey } = await api.post<UploadUrlResponse>(
        '/api/course-materials/upload-url',
        {
          courseLessonId,
          type,
          title,
          description,
          fileName: file.name,
          contentType: file.type,
          fileSize: file.size,
        },
      );
      materialIdRef.current = materialId;

      // Step 2 — PUT file to R2 via presigned URL (XHR for progress tracking)
      setStatus('uploading');
      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', uploadUrl, true);
        xhr.setRequestHeader('Content-Type', file.type);

        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) {
            setProgress(Math.round((e.loaded / e.total) * 100));
          }
        };

        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            setProgress(100);
            resolve();
          } else {
            reject(new Error(`Upload to R2 failed: HTTP ${xhr.status}`));
          }
        };

        xhr.onerror = () => reject(new Error('Upload network error'));
        xhr.ontimeout = () => reject(new Error('Upload timed out'));
        xhr.send(file);
      });

      // Step 3 — Confirm upload
      setStatus('confirming');
      await api.post<void>(`/api/course-materials/${materialId}/confirm`, { fileKey });

      setStatus('done');
      void qc.invalidateQueries({ queryKey: materialKeys.all });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Lỗi tải lên, thử lại sau.';
      setError(message);
      setStatus('error');

      // Best-effort rollback: delete the orphaned material record
      if (materialIdRef.current) {
        try {
          await api.delete(`/api/course-materials/${materialIdRef.current}`);
        } catch {
          // Ignore rollback error — server cron cleans orphans
        }
      }
    }
  }

  return { upload, progress, status, error, reset };
}
